// src/ui/bubbles/bubble-priority.ts — Phase 6 D-02 tier preempt logic.
//
// Pure utility — no imports beyond TypeScript types. Drives BubbleLayer's
// concurrent-bubble cap with the four-tier priority model from CONTEXT D-02:
//
//   Critical    — catch / free-all / foxguard-called / fear-threshold-crossed
//   Shout       — shout (after the P4 stability check)
//   Flavour     — every other per-character `eventTag` quote
//   Commentary  — adult-commented (lowest)
//
// When `bubbles.length < cap`: incoming joins. When the cap is hit:
//   - same-or-lower-tier incoming → dropped silently (no queue, no FIFO)
//   - higher-tier incoming → preempts the OLDEST bubble at the LOWEST tier
//     currently visible.
//
// `cap` is passed in (not baked) so unit tests stay decoupled from
// P6_BUBBLE_CAP and so future tuning (cap-2 ruthless / cap-5 queued) is
// trivial to swap.

export type BubbleTier = 'critical' | 'shout' | 'flavour' | 'commentary';

/** Lower number = higher priority. Tier ordering is the D-02 contract. */
export const TIER_RANK: Record<BubbleTier, number> = {
  critical: 0,
  shout: 1,
  flavour: 2,
  commentary: 3,
};

export interface BubbleEntry {
  id: string;
  tier: BubbleTier;
  /** `performance.now()` at admit time — drives oldest-at-lowest-tier eviction. */
  mountedAt: number;
  entityId: string;
  characterId?: string;
  text: string;
  /** Total lifetime in ms before auto-expire — DIAL-07 derived from waffle length. */
  durationMs: number;
}

/**
 * Append `incoming` to `bubbles` if doing so respects `cap` and the D-02
 * preempt rules. Returns a NEW array (immutable contract — callers feed this
 * directly into a React `setState` updater).
 *
 * Rules (D-02):
 *   1. Under cap                     → append.
 *   2. At cap, same-or-lower tier    → drop silently (return original array).
 *   3. At cap, strictly higher tier  → evict the oldest entry at the lowest
 *                                       tier currently visible; append new.
 *
 * "Higher tier" means lower TIER_RANK number. Ties on tier OR equal-rank-or-
 * higher (greater rank number) are dropped — never preempt at the same tier.
 */
export function tryAdd(
  bubbles: BubbleEntry[],
  incoming: BubbleEntry,
  cap: number,
): BubbleEntry[] {
  if (bubbles.length < cap) return [...bubbles, incoming];

  // Find the lowest tier (highest rank number) currently visible.
  let lowestRank = -1;
  for (const b of bubbles) {
    const r = TIER_RANK[b.tier];
    if (r > lowestRank) lowestRank = r;
  }

  // Same-or-lower tier (rank >= lowestRank) → drop silently.
  if (TIER_RANK[incoming.tier] >= lowestRank) {
    return bubbles;
  }

  // Higher tier preempts the OLDEST bubble at the lowest tier.
  let oldestIdx = -1;
  let oldestTime = Infinity;
  for (let i = 0; i < bubbles.length; i++) {
    const b = bubbles[i]!;
    if (TIER_RANK[b.tier] === lowestRank && b.mountedAt < oldestTime) {
      oldestIdx = i;
      oldestTime = b.mountedAt;
    }
  }

  // Defensive: if we somehow couldn't find a victim (shouldn't happen because
  // bubbles.length === cap and lowestRank is a real rank from the list), drop.
  if (oldestIdx === -1) return bubbles;

  return [...bubbles.slice(0, oldestIdx), ...bubbles.slice(oldestIdx + 1), incoming];
}
